import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/client/react";
import { readFragment } from "gql.tada";
import { useStore } from "@/app/store";
import {
  CALCULATION_DETAIL_FRAGMENT,
  GET_CALCULATION_DETAIL,
  GET_CATEGORY_DEFINITIONS_FOR_DETAIL,
} from "../graphql/queries";
import type { Calculation } from "../store/calculationDetailSlice";

export type CalculationDetail = Calculation;
export type CalculationCategoryResult = CalculationDetail["categories"][number];

export interface Subcategory {
  id: string;
  name: string;
}

export interface CategoryGroup {
  id: string;
  name: string;
  subcategories: Subcategory[];
}

const useCalculationDetail = (calculationId: string) => {
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");
  const [selectedSubcategoryId, setSelectedSubcategoryId] =
    useState<string>("");

  const setCalculation = useStore((s) => s.setCalculation);
  const calculation = useStore((s) =>
    s.calculation?.id === calculationId ? s.calculation : null,
  );

  const {
    data: calculationData,
    loading: calcLoading,
    error: calculationError,
    refetch: refetchCalculation,
  } = useQuery(GET_CALCULATION_DETAIL, {
    variables: { id: calculationId },
  });

  useEffect(() => {
    if (!calculationData?.calculation) return;
    const next = readFragment(
      CALCULATION_DETAIL_FRAGMENT,
      calculationData.calculation,
    );
    setCalculation(next, calculationId);
  }, [calculationData, calculationId, setCalculation]);

  const { data: categoriesData, loading: catsLoading } = useQuery(
    GET_CATEGORY_DEFINITIONS_FOR_DETAIL,
  );

  const categoryGroups = useMemo<CategoryGroup[]>(() => {
    if (!calculation || !categoriesData) return [];

    const selectedIds = new Set(
      calculation.categories.map((c) => c.subCategoryDefinitionId),
    );

    return categoriesData.calculationCategoryDefinitions
      .map((catDef) => ({
        id: catDef.id,
        name: catDef.name,
        subcategories: catDef.subCategories
          .filter((sub) => selectedIds.has(sub.id))
          .map((sub) => ({ id: sub.id, name: sub.name })),
      }))
      .filter((group) => group.subcategories.length > 0);
  }, [calculation, categoriesData]);

  const activeCategoryId = selectedCategoryId || categoryGroups[0]?.id || "";
  const activeSubcategoryId =
    selectedSubcategoryId ||
    categoryGroups.find((g) => g.id === activeCategoryId)?.subcategories[0]
      ?.id ||
    "";

  const handleCategoryChange = useCallback((id: string) => {
    setSelectedCategoryId(id);
    setSelectedSubcategoryId("");
  }, []);

  const handleSubcategoryChange = useCallback((id: string) => {
    setSelectedSubcategoryId(id);
  }, []);

  const loading = calcLoading || catsLoading;

  return {
    categoryGroups,
    activeCategoryId,
    activeSubcategoryId,
    loading,
    error: calculationError,
    refetchCalculation,
    onCategoryChange: handleCategoryChange,
    onSubcategoryChange: handleSubcategoryChange,
  };
};

export default useCalculationDetail;
